import { z } from 'zod';
import { SynthesisFileInputSchema } from './plan';

export const SynthesisFileStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
]);
export type SynthesisFileStatus = z.infer<typeof SynthesisFileStatusSchema>;

export const SynthesisStageSchema = z.enum([
  'pending',      // legacy: written by createSynthesisJob at enqueue time
  'queued',
  // Stage englobante ouverte par synthesis_service.processSingleDocument pour
  // toute la durée d'une pièce (onStageStart/'processing' → onStageEnd). Elle
  // manquait à l'enum alors qu'elle est la PREMIÈRE valeur écrite en Redis pour
  // chaque fichier : tout re-run BullMQ (stall / crash worker) relisait donc un
  // jobData invalide et tuait le batch en UnrecoverableError. Incident du
  // 2026-08-01 (dépôt de 240 pièces en 4 min) : 207 pièces jamais enrichies.
  'processing',
  'fetch_document',
  'download_buffer',
  'extract_text',
  'title_description',
  'synthesis_llm',
  'create_docx',
  'upload_storage',
  'vectorize',
  'completed',
  'failed',
]);
export type SynthesisStage = z.infer<typeof SynthesisStageSchema>;

export const BatchStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'partially_completed',
  'failed',
]);
export type BatchStatus = z.infer<typeof BatchStatusSchema>;

/**
 * Per-file state persisted in Redis as part of the synthesis job data.
 * Used by filterPendingFiles and updateSynthesisFile.
 *
 * Nullable fields match the legacy `job_manager.ts` writes (null, not undefined).
 */
/**
 * The processing_plan stored in Redis by createSynthesisJob. It copies all
 * SynthesisFileInput fields EXCEPT documentID (stored separately as document_id
 * at the file level) and dossierID (already at job level as case_id).
 * Both are omitted from the plan because they are redundant with the parent keys.
 */
const StoredProcessingPlanSchema = SynthesisFileInputSchema
  .omit({ documentID: true })
  .extend({
    // dossierID is always present in the stored plan (copied from the request)
    dossierID: z.string().uuid(),
  })
  .passthrough();

export const SynthesisFileDataSchema = z.object({
  fileIndex: z.number().int().nonnegative(),
  document_id: z.string().uuid(),
  processing_plan: StoredProcessingPlanSchema,
  status: SynthesisFileStatusSchema,
  // `.catch(null)` — DÉFENSE EN PROFONDEUR : `stage` est un champ d'AFFICHAGE
  // (progression Bull Board / front). La reprise crash-safe s'appuie sur
  // `status` (voir filterPendingFiles), jamais sur `stage`. Une valeur inconnue
  // ici ne doit donc JAMAIS faire échouer le parse du batch entier : elle
  // dégrade en `null` au lieu de transformer le jobData en pilule empoisonnée
  // au re-run (incident 2026-08-01, cf. commentaire de SynthesisStageSchema).
  // Les champs qui pilotent réellement le traitement (ids, status, files)
  // restent validés durement.
  stage: SynthesisStageSchema.nullable().optional().catch(null),
  progress: z.number().int().min(0).max(100).nullable().optional(),
  retry_count: z.number().int().nonnegative().default(0),
  started_at: z.number().int().nullable().optional(),
  completed_at: z.number().int().nullable().optional(),
  error_message: z.string().nullable().optional(),
  result: z.unknown().nullable().optional(),
}).passthrough();
export type SynthesisFileData = z.infer<typeof SynthesisFileDataSchema>;

/**
 * Top-level synthesis batch job state persisted in Redis.
 *
 * IMPORTANT: nullable fields use `.nullable().optional()` because `job_manager.ts`
 * writes `null` (not `undefined`) for `error`, `current_batch_region`, and similar.
 * Zod `.optional()` alone accepts `undefined` / missing key but NOT `null`.
 *
 * `user_id` is nested under `request_params.userId` in the legacy shape written by
 * `createSynthesisJob` — we accept it as optional at the top level AND read with a
 * fallback (see processor Task 1.8 Step 2). Task 1.12 lifts it to top-level for new
 * jobs so the fallback is only needed during the deploy window.
 */
export const SynthesisJobDataSchema = z.object({
  jobId: z.string().uuid(),
  case_id: z.string().uuid(),
  cabinet_id: z.string().uuid(),
  user_id: z.string().uuid().optional(),
  total_files: z.number().int().positive().max(10),
  files_completed: z.number().int().nonnegative().default(0),
  files_failed: z.number().int().nonnegative().default(0),
  status: BatchStatusSchema,
  stage: z.string().nullable().optional(),
  progress: z.number().int().min(0).max(100).default(0),
  current_batch_region: z.string().nullable().optional(),
  created_at: z.number().int(),
  updated_at: z.number().int().nullable().optional(),
  error: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  request_params: z.record(z.string(), z.unknown()).nullable().optional(),
  files: z.array(SynthesisFileDataSchema),
}).passthrough(); // tolerate extra fields written by job_manager we don't care about
export type SynthesisJobData = z.infer<typeof SynthesisJobDataSchema>;

/**
 * BullMQ job.data payload for the 'synthesis-batch' queue.
 * Parsed at processor entry to enforce the Redis → worker boundary.
 *
 * `userId` is optional because legacy jobs enqueued before PR1 only carried
 * `{ jobId, requestId }`. The processor falls back to `jobData.user_id` (or
 * `jobData.request_params.userId`) when `payload.userId` is absent, so the
 * ALS context still gets enriched correctly during the deploy window.
 */
export const BullMQSynthesisPayloadSchema = z.object({
  jobId: z.string().uuid(),
  requestId: z.string().optional(),
  userId: z.string().uuid().optional(),
}).passthrough();
export type BullMQSynthesisPayload = z.infer<typeof BullMQSynthesisPayloadSchema>;
